import React, { Fragment, useState, useRef } from 'react';
import type { Dispatch, SetStateAction, ReactNode, MutableRefObject, RefObject } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import LoadingButton from '@atlaskit/button/loading-button';
import Image from '@atlaskit/image';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { NetworkError } from '@confluence/network';

import NoResultsImage from '../../assets/NoResults.svg';

import { getUnsplashPhotos } from './unsplashAPI';
import type { UnsplashResult } from './unsplashAPI';
import type { SelectedImage } from './UnsplashSearch';

type UnsplashSearchContentProps = {
	unSplashSearchResults: UnsplashResult[] | undefined;
	idPrefix: string;
	handleImageSelection: (props: SelectedImage) => void;
	loadMoreText: Record<string, ReactNode>;
	queryWord: MutableRefObject<string>;
	pageNumber: MutableRefObject<number>;
	setUnsplashSearchResults: Dispatch<SetStateAction<UnsplashResult[] | undefined>>;
	handleError: (error: NetworkError) => void;
	searchResultsRef: RefObject<HTMLDivElement>;
	unsplashResultsCountFromLastAPICall: number;
	setUnsplashResultsCountFromLastAPICall: Dispatch<SetStateAction<number>>;
	dropdownPopupComponentRef?: RefObject<HTMLElement | null>;
	isPageTitleComponent?: boolean;
	analyticsSource: string;
};

export const CARDS_DEFAULT_UNSPLASH_QUERY_WORD = 'pastel gradient';
export const PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD = 'color';

const UNSPLASH_LICENSE_LINK = 'https://unsplash.com/license';

const UNSPLASH_TERMS_OF_SERVICE_LINK = 'https://unsplash.com/terms';

const i18n = defineMessages({
	unsplashNoResultsText: {
		id: 'custom-sites-extensions.unsplash.search.no.results.text',
		defaultMessage: 'Nothing matches your search',
		description: 'Text to inform when there are no Unsplash image results to render',
	},
	unsplashNoMoreResultsToLoadText: {
		id: 'custom-sites-extensions.unsplash.search.no.more.results.to.load.text',
		defaultMessage: 'No more matches try another search',
		description: 'Text to inform when there are no more Unsplash image results to load',
	},
	unsplashLegal: {
		id: 'custom-sites-extensions.unsplash.legal',
		defaultMessage:
			'By using images from Unsplash, you agree to their {unsplashLicenseLink} and {unsplashTermsOfServiceLink}.',
		description: 'Text that explains the Unsplash legal agreements (license and terms of service)',
	},
	unsplashLicenseLink: {
		id: 'custom-sites-extensions.unsplash.license',
		defaultMessage: 'license',
		description: 'Text that links to Unsplash license',
	},
	unsplashTermsOfServiceLink: {
		id: 'custom-sites-extensions.unsplash.terms.of.service.',
		defaultMessage: 'Terms of Service',
		description: 'Text that links to Unsplash Terms of Service',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchContentContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	maxWidth: '264.14px !important',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchResultsContainer = styled.div({
	display: 'grid',
	maxHeight: '390px',
	overflow: 'scroll',
	gridTemplateColumns: 'auto auto',
	gap: token('space.100'),
	padding: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashWrapper = styled.div({
	display: 'inline-grid',
	position: 'relative',
	width: '120.07px',
});

const artistInfoSelector = 'link-cards.ArtistInfo';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArtistInfo = styled.div<{ 'data-styled-selector'?: string }>({
	background: token('color.background.inverse.subtle.hovered'),
	borderBottomLeftRadius: '3px',
	borderBottomRightRadius: '3px',
	position: 'absolute',
	bottom: 0,
	width: '100%',
	font: token('font.body.small'),
	opacity: 1,
	visibility: 'hidden',
	'&:hover': {
		visibility: 'visible',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArtistLink = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	textDecoration: 'none !important',
});

ArtistInfo.defaultProps = {
	'data-styled-selector': artistInfoSelector,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArtistNameWrapper = styled.div({
	marginLeft: token('space.050'),
	color: token('color.text.inverse'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const baseImageStyles = {
	width: '100%',
	height: '77.93px',
	borderRadius: '3px',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashImage = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...baseImageStyles,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashImageWrapper = styled.button({
	border: 'none',
	background: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	[`&:hover + [data-styled-selector="${artistInfoSelector}"]`]: {
		visibility: 'visible',
	},
	cursor: 'pointer',
	padding: 0,
	height: '77.93px',
	outlineColor: token('color.border.information'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashLegalTerms = styled.div({
	padding: `${token('space.100')} 0`,
	backgroundColor: token('color.background.accent.gray.subtlest'),
	borderBottomLeftRadius: '3px',
	borderBottomRightRadius: '3px',
	color: token('color.text.subtle'),
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegalTextWrapper = styled.div({
	margin: `${token('space.0')} ${token('space.100')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnderlinedLink = styled.a({
	textDecoration: 'underline',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	color: `${token('color.text.subtlest')} !important`, // Using color.text.subtlest here because color.link is blue
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EndofResultsWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	gridColumnStart: 'span 2',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashSearchErrorWrapper = styled.div({
	display: 'flex',
	height: '164px',
	gridColumn: 'span 3',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchErrorText = styled.div({
	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImagePlaceholder = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...baseImageStyles,
	backgroundColor: token('color.background.neutral'),
});

const UnsplashLicenseLink = ({ link }) => (
	<UnderlinedLink target="_blank" href={link}>
		<FormattedMessage {...i18n.unsplashLicenseLink} />
	</UnderlinedLink>
);

const UnsplashTermsOfServiceLink = ({ link }) => (
	<UnderlinedLink target="_blank" href={link}>
		<FormattedMessage {...i18n.unsplashTermsOfServiceLink} />
	</UnderlinedLink>
);

export const UnsplashSearchContent: React.FC<UnsplashSearchContentProps> = ({
	unSplashSearchResults,
	idPrefix,
	handleImageSelection,
	loadMoreText,
	queryWord,
	pageNumber,
	setUnsplashSearchResults,
	handleError,
	searchResultsRef,
	unsplashResultsCountFromLastAPICall,
	setUnsplashResultsCountFromLastAPICall,
	dropdownPopupComponentRef,
	isPageTitleComponent,
	analyticsSource,
}) => {
	const [showButtonSpinner, setShowButtonSpinner] = useState<boolean>(false);
	const imageRef = useRef<HTMLButtonElement>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onImageClick = ({ fullSizeImageUrl, altDescription, downloadLink }: SelectedImage) => {
		handleImageSelection({ fullSizeImageUrl, altDescription, downloadLink });

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'selected',
				actionSubject: 'image',
				source: analyticsSource,
				attributes: {
					imageSource: 'unsplashSearch',
				},
			},
		}).fire();
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, result: UnsplashResult) => {
		if (e.key === 'Enter' || e.key === ' ') {
			onImageClick(result);
		}
	};

	const onClickLoadMoreButton = () => {
		setShowButtonSpinner(true);
		pageNumber.current++;
		const defaultQueryWord = isPageTitleComponent
			? PAGE_TITLE_DEFAULT_UNSPLASH_QUERY_WORD
			: CARDS_DEFAULT_UNSPLASH_QUERY_WORD;
		const queryKeyword = queryWord.current || defaultQueryWord;
		void getUnsplashPhotos(queryKeyword, pageNumber.current, isPageTitleComponent)
			.then((results) => {
				setUnsplashResultsCountFromLastAPICall(results.length);
				setUnsplashSearchResults(unSplashSearchResults!.concat(results));
				setShowButtonSpinner(false);
				imageRef.current?.focus();
			})
			.catch((error) => {
				handleError(error);
			});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'loadMoreUnsplashImagesButtonClicked',
				source: analyticsSource,
			},
		}).fire();
	};

	const UnsplashLegalText = () => {
		return (
			<LegalTextWrapper>
				<FormattedMessage
					{...i18n.unsplashLegal}
					values={{
						unsplashLicenseLink: <UnsplashLicenseLink link={UNSPLASH_LICENSE_LINK} />,
						unsplashTermsOfServiceLink: (
							<UnsplashTermsOfServiceLink link={UNSPLASH_TERMS_OF_SERVICE_LINK} />
						),
					}}
				/>
			</LegalTextWrapper>
		);
	};

	return (
		<SearchContentContainer
			ref={dropdownPopupComponentRef as RefObject<HTMLDivElement>}
			onClick={(e) => e.stopPropagation()}
		>
			<SearchResultsContainer ref={searchResultsRef}>
				{unSplashSearchResults?.length ? (
					<Fragment>
						{unSplashSearchResults.map((result, index) =>
							result ? (
								<UnsplashWrapper
									key={index}
									data-testid={`${idPrefix}-unsplash-image-${result.id}`}
									onKeyDown={(e) => onKeyDown(e, result)}
								>
									<UnsplashImageWrapper
										// assign ref to image result only when it is the first of 30 new images loaded more.
										// This ref is necessary for placing focus on the image result away from load more button
										ref={index === unSplashSearchResults.length - 30 ? imageRef : null}
										onClick={() => onImageClick(result)}
									>
										<UnsplashImage src={result.smallSizeImageUrl} alt={result.altDescription} />
									</UnsplashImageWrapper>
									<ArtistInfo>
										<ArtistLink href={result.artistUrl} target="_blank">
											<ArtistNameWrapper>{result.artistName}</ArtistNameWrapper>
										</ArtistLink>
									</ArtistInfo>
								</UnsplashWrapper>
							) : (
								<ImagePlaceholder key={index} data-testid="unsplash-popup-image-placeholder" />
							),
						)}
						<EndofResultsWrapper>
							{unsplashResultsCountFromLastAPICall === 30 ? (
								<LoadingButton
									onClick={onClickLoadMoreButton}
									spacing="compact"
									isLoading={showButtonSpinner}
								>
									<FormattedMessage {...loadMoreText} />
								</LoadingButton>
							) : (
								<SearchErrorText>
									<FormattedMessage {...i18n.unsplashNoMoreResultsToLoadText} />
								</SearchErrorText>
							)}
						</EndofResultsWrapper>
					</Fragment>
				) : (
					<UnsplashSearchErrorWrapper>
						<Image src={NoResultsImage} alt="unsplash search no results" />
						<SearchErrorText>
							<FormattedMessage {...i18n.unsplashNoResultsText} />
						</SearchErrorText>
					</UnsplashSearchErrorWrapper>
				)}
			</SearchResultsContainer>
			<UnsplashLegalTerms>
				<UnsplashLegalText />
			</UnsplashLegalTerms>
		</SearchContentContainer>
	);
};
